import config from 'Cloud/config';
import { abTrialFastCheckout } from 'reactApp/appHelpers/featuresHelpers/features/trialFastchecout';
import { renderBuyModal } from 'reactApp/components/BuyModal/renderBuyModal';
import { openFastCheckout as openFastCheckoutAction, openTariffBuy } from 'reactApp/modules/payment/payment.module';
import { productsController } from 'reactApp/modules/products/products.controller';
import { updateProducts } from 'reactApp/modules/products/products.module';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import { waitForUserUpdate } from 'reactApp/modules/user/waitForUserUpdate';
import { store } from 'reactApp/store';
import { noopVoid } from 'reactApp/utils/helpers';
import { openSubscriptionsPage } from 'reactApp/utils/openHelpers';

const BILLING_ENABLED = config.get('user.cloud.billing.enabled');

const openFastCheckout = ({
    productId,
    onSuccess = noopVoid,
    afterUpdate = noopVoid,
    onClose = noopVoid,
    onNoTariff = noopVoid,
    paySource,
    hideSwitchSidebarBlock = false,
}) => {
    return new Promise((resolve) => {
        const afterUpdateCallback = () => {
            afterUpdate();
            resolve(true);
        };

        const onCloseCallback = () => {
            onClose();
            resolve(false);
        };

        store.dispatch(
            openFastCheckoutAction({
                productId,
                onSuccess,
                onClose: onCloseCallback,
                onNoTariff,
                afterUpdate: afterUpdateCallback,
                paySource,
                hideSwitchSidebarBlock,
            })
        );
    });
};

const openTariff = ({ id, onSuccess, afterUpdate = noopVoid, source = '', paySource, title, description, showBuyModal }) => {
    return new Promise((resolve) => {
        const onSuccessCallback = () => {
            onSuccess();
            // для другого окна обновление будет внутри саги
            if (showBuyModal) {
                store.dispatch(updateProducts());
            }
            waitForUserUpdate().then(() => {
                afterUpdate();
                resolve(true);
            });
        };

        const onClose = () => {
            resolve(false);
        };

        if (showBuyModal) {
            renderBuyModal({
                id,
                onSuccess: onSuccessCallback,
                onClose,
                source,
                paySource,
                title,
                description,
            });
            return;
        }

        store.dispatch(
            openTariffBuy({
                productId: id,
                onSuccess: onSuccessCallback,
                onClose,
                source,
                paySource,
                title,
                paymentDescription: description,
            })
        );
    });
};

interface RequestPaymentProps {
    tariff?: string;
    productId?: string;
    showTariffList?: boolean;
    showTariffListOnNoTariffs?: boolean;
    forceFastCheckout?: boolean;
    onSuccess?: () => void;
    afterUpdate?: () => void;
    onClose?: () => void;
    goToSubscriptions?: typeof openSubscriptionsPage;
    isMobile?: boolean;
    source?: string;
    paySource?: string;
    query?: string;
    title?: string;
    description?: string;
}

export const requestPayment = (props: RequestPaymentProps = {}) => {
    const {
        tariff,
        productId,
        forceFastCheckout, // TODO: починить флаг, https://jira.vk.team/browse/CLOUDWEB-18916
        paySource,
        query,
        showTariffList = false,
        showTariffListOnNoTariffs = false,
        onSuccess = noopVoid,
        afterUpdate = noopVoid,
        onClose = noopVoid,
        goToSubscriptions = openSubscriptionsPage,
        isMobile = false,
        source = '',
        title = '',
        description = '',
    } = props;

    if (!BILLING_ENABLED) {
        return Promise.resolve();
    }

    if (isMobile) {
        goToSubscriptions({ inSameTab: true, query: tariff ? `action=request-payment&productId=${tariff}` : '' });
        return Promise.resolve();
    }

    if (!(tariff || productId) && !forceFastCheckout) {
        goToSubscriptions({ inSameTab: true, anchor: 'special' });
        return Promise.resolve();
    }

    return productsController
        .loadProducts()
        .then(() => {
            const product = ProductsSelectors.getProductById(store.getState(), productId);

            // tempexp_18329-start
            if (product && product.hasTrial && abTrialFastCheckout === 'b') {
                return openFastCheckout({ productId, onSuccess, afterUpdate, onClose, paySource, hideSwitchSidebarBlock: true });
            }
            // tempexp_18329-end

            if (productId) {
                return openTariff({
                    id: productId,
                    onSuccess,
                    afterUpdate,
                    source,
                    paySource,
                    title,
                    description,
                    showBuyModal: Boolean(product && product.hasTrial),
                });
            }

            const onNoTariff = showTariffListOnNoTariffs ? () => openSubscriptionsPage({ query }) : undefined;

            if (!showTariffList || tariff) {
                return openFastCheckout({ productId: tariff, onSuccess, afterUpdate, onClose, paySource, onNoTariff });
            }

            openSubscriptionsPage({ query });
        })
        .catch(() => openSubscriptionsPage({ query }));
};
